import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  FileSearch,
  FileCheck,
  Handshake,
  Target,
  TrendingUp,
  Users,
  Building2,
  CheckCircle,
  ClipboardList,
  Scale,
  FileText,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const MA = () => {
  const { t } = useLanguage();

  const processSteps = [
    { icon: <FileSearch className="h-6 w-6" />, titleKey: 'maPage.step1', descKey: 'maPage.step1Desc' },
    { icon: <Target className="h-6 w-6" />, titleKey: 'maPage.step2', descKey: 'maPage.step2Desc' },
    { icon: <Handshake className="h-6 w-6" />, titleKey: 'maPage.step3', descKey: 'maPage.step3Desc' },
    { icon: <TrendingUp className="h-6 w-6" />, titleKey: 'maPage.step4', descKey: 'maPage.step4Desc' },
  ];

  const services = [
    { icon: <FileSearch className="h-5 w-5" />, textKey: 'maPage.service1' },
    { icon: <FileText className="h-5 w-5" />, textKey: 'maPage.service2' },
    { icon: <Handshake className="h-5 w-5" />, textKey: 'maPage.service3' },
    { icon: <ClipboardList className="h-5 w-5" />, textKey: 'maPage.service4' },
    { icon: <FileCheck className="h-5 w-5" />, textKey: 'maPage.service5' },
    { icon: <CheckCircle className="h-5 w-5" />, textKey: 'maPage.service6' },
  ];

  const valuationSteps = [
    'maPage.valuationStep1',
    'maPage.valuationStep2',
    'maPage.valuationStep3',
    'maPage.valuationStep4',
    'maPage.valuationStep5'
  ];

  const creditTypes = [
    { titleKey: 'maPage.credit1', descKey: 'maPage.credit1Desc' },
    { titleKey: 'maPage.credit2', descKey: 'maPage.credit2Desc' },
    { titleKey: 'maPage.credit3', descKey: 'maPage.credit3Desc' },
    { titleKey: 'maPage.credit4', descKey: 'maPage.credit4Desc' },
    { titleKey: 'maPage.credit5', descKey: 'maPage.credit5Desc' },
    { titleKey: 'maPage.credit6', descKey: 'maPage.credit6Desc' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="ma-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-5"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1580506904550-7c5a97a4cc90?w=1920&q=80)'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('maPage.heroTag')}
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
                {t('maPage.heroTitle')}{' '}
                <span className="gold-sheen">{t('maPage.heroHighlight')}</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                {t('maPage.heroDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                  data-testid="ma-cta-primary"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('maPage.ctaPrimary')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-bronze/10 rounded-sm blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&q=80"
                  alt="M&A Meeting"
                  className="relative rounded-sm shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-emerald">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: 'R$ 2bi+', labelKey: 'maPage.statsOperations' },
              { number: '200+', labelKey: 'maPage.statsPartners' },
              { number: '50+', labelKey: 'maPage.statsProjects' },
              { number: '20+', labelKey: 'maPage.statsExperience' },
            ].map((stat, index) => (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-serif font-bold text-white mb-2">
                  {stat.number}
                </p>
                <p className="text-stone-300 text-sm">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 bg-white" data-testid="ma-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('maPage.methodologyTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('maPage.methodologyTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative text-center"
                data-testid={`ma-step-${index}`}
              >
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-stone-200" />
                )}
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-emerald text-white flex items-center justify-center mx-auto mb-4 relative z-10">
                    {step.icon}
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-bronze text-white text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                  {t(step.titleKey)}
                </h3>
                <p className="text-stone-600 text-sm">
                  {t(step.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="ma-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('maPage.servicesTag')}
              </span>
              <h2 className="text-3xl font-serif font-bold text-stone-900 mb-8">
                {t('maPage.servicesTitle')}
              </h2>
              <div className="grid grid-cols-1 gap-4">
                {services.map((service) => (
                  <div 
                    key={service.textKey}
                    className="flex items-center gap-4 bg-white p-4 rounded-sm border border-stone-200"
                  >
                    <div className="w-10 h-10 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald">
                      {service.icon}
                    </div>
                    <span className="text-stone-700 font-medium">{t(service.textKey)}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('maPage.valuationTag')}
              </span>
              <h2 className="text-3xl font-serif font-bold text-stone-900 mb-8">
                {t('maPage.valuationTitle')}
              </h2>
              <p className="text-stone-600 mb-6">
                {t('maPage.valuationDesc')}
              </p>
              <div className="bg-emerald p-6 rounded-sm text-white">
                <h3 className="font-serif font-semibold mb-4">{t('maPage.valuationStepsTitle')}</h3>
                <div className="space-y-3">
                  {valuationSteps.map((stepKey, index) => (
                    <div key={stepKey} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-bronze text-white text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="text-stone-200">{t(stepKey)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credit Operations */}
      <section className="py-24 md:py-32 bg-white" data-testid="credit-operations">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('maPage.creditTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('maPage.creditTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('maPage.creditDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {creditTypes.map((credit, index) => (
              <motion.div
                key={credit.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-stone-50 p-6 rounded-sm border border-stone-200 hover:shadow-md transition-shadow"
                data-testid={`credit-type-${index}`}
              >
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                  {t(credit.titleKey)}
                </h3>
                <p className="text-stone-600 text-sm">
                  {t(credit.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Network */}
      <section className="py-24 md:py-32 bg-emerald text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('maPage.networkTag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                {t('maPage.networkTitle')}
              </h2>
              <p className="text-stone-300 mb-8 leading-relaxed">
                {t('maPage.networkDesc')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Building2 className="h-6 w-6" />, labelKey: 'maPage.privateEquity' },
                  { icon: <TrendingUp className="h-6 w-6" />, labelKey: 'maPage.ventureCapital' },
                  { icon: <Users className="h-6 w-6" />, labelKey: 'maPage.familyOffices' },
                  { icon: <Briefcase className="h-6 w-6" />, labelKey: 'maPage.strategicInvestors' },
                ].map((item) => (
                  <div key={item.labelKey} className="flex items-center gap-3 bg-white/10 p-4 rounded-sm">
                    <span className="text-bronze">{item.icon}</span>
                    <span className="text-sm font-medium">{t(item.labelKey)}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm">
                <Scale className="h-16 w-16 text-bronze mx-auto mb-4" />
                <h3 className="text-2xl font-serif font-bold mb-4">
                  {t('maPage.diagnosisTitle')}
                </h3>
                <p className="text-stone-300 mb-6">
                  {t('maPage.diagnosisDesc')}
                </p>
                <Button 
                  asChild
                  className="bg-bronze hover:bg-bronze-light text-white rounded-sm px-8 py-4"
                  data-testid="ma-diagnosis-btn"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('maPage.diagnosisBtn')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('maPage.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('maPage.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="ma-final-cta"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('maPage.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MA;
